import { AddMediaElementCommand } from './AddMediaElementCommand';
import { VideoEditor } from '../VideoEditor';
import { Element } from '../models/Element';
import { Track } from '../models/Track';
import { TimelineMode } from './TimelineMode';
import { VideoEditorLogger } from '../../utility/VideoEditorLogger';

export interface AddVideoElementOptions {
  /**
   * Default volume for the video element (0-100)
   */
  volume?: number;
  
  /**
   * How the video should be resized to fit the element ('cover', 'contain', 'fill')
   */
  fit?: 'cover' | 'contain' | 'fill';
  
  /**
   * Whether the video should loop when it reaches the end
   */
  loop?: boolean;
  
  /**
   * Whether to automatically add fade in/out effects for audio
   */
  autoFade?: boolean;
}

/**
 * Specialized command for adding video elements to the timeline.
 * Extends AddMediaElementCommand with video-specific features:
 * - Volume control: sets initial volume level
 * - Video fit: controls how video is resized to fit the element
 * - Loop control: enables/disables video looping
 * - Auto fade: applies fade in/out effects to video audio
 * - Smart track placement: prefers video tracks and handles collision detection
 */
export class AddVideoElementCommand extends AddMediaElementCommand {
  private options: AddVideoElementOptions;

  constructor(
    editor: VideoEditor,
    element: Element,
    targetTrackId?: string,
    mode: TimelineMode = TimelineMode.DEFAULT,
    options: AddVideoElementOptions = {}
  ) {
    super(editor, element, targetTrackId, mode);
    // Only store options that were explicitly provided
    this.options = options;
  }

  execute(): boolean {
    VideoEditorLogger.logCommand(this, 'start', {
      elementId: this.element.id,
      elementType: this.element.type,
      targetTrackId: this.targetTrackId,
      mode: this.mode,
      options: this.options,
    });

    // Apply video-specific modifications before executing parent logic
    if (!this.applyVideoSpecificModifications()) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to apply video-specific modifications',
      });
      return false;
    }

    // Call parent execute which handles the core adding logic
    return super.execute();
  }

  getDescription(): string {
    const baseDescription = super.getDescription();
    const videoFeatures: string[] = [];
    
    if (this.options.volume !== undefined) {
      videoFeatures.push(`volume: ${this.options.volume}%`);
    }
    if (this.options.fit !== undefined) {
      videoFeatures.push(`fit: ${this.options.fit}`);
    }
    if (this.options.loop) {
      videoFeatures.push('loop');
    }
    if (this.options.autoFade) {
      videoFeatures.push('auto-fade');
    }

    if (videoFeatures.length > 0) {
      return `${baseDescription} [${videoFeatures.join(', ')}]`;
    }

    return baseDescription;
  }

  /**
   * Override the findOrCreateTargetTrack method to implement video-specific logic
   */
  protected findOrCreateTargetTrack(): Track | undefined {
    // If specific track ID provided, use it (unless it has collisions in DEFAULT mode)
    if (this.targetTrackId) {
      const track = this.editor.getTrackById(this.targetTrackId);
      if (track) {
        // In DEFAULT mode, if target track has collision, create new track
        if (this.mode === TimelineMode.DEFAULT && this.hasCollision(track)) {
          VideoEditorLogger.logTimelineOperation('add_video_element_collision', {
            message: `Target track ${this.targetTrackId} has collision, creating new track`,
          });
          return this.createNewTrack();
        }
        return track;
      }
      VideoEditorLogger.logTimelineOperation('add_video_element_track_not_found', {
        warning: `Target track ${this.targetTrackId} not found, will auto-select`,
      });
    }

    // Auto-select compatible track based on video-specific logic
    const compatibleTrack = this.findCompatibleVideoTrack();
    if (compatibleTrack) {
      return compatibleTrack;
    }

    // Create new track if no compatible track found
    return this.createNewTrack();
  }

  /**
   * Video-specific track finding logic to prefer video tracks
   */
  private findCompatibleVideoTrack(): Track | undefined {
    // Only look for video tracks for video elements
    const videoTracks = this.editor.timeline.tracks.filter(
      (track) => track.type === 'video'
    );

    if (videoTracks.length === 0) {
      return undefined;
    }

    // For DEFAULT mode, try to find a track with no collisions
    if (this.mode === TimelineMode.DEFAULT) {
      // First, try to find a track where we can fit without collisions
      for (const track of videoTracks) {
        if (!this.hasCollision(track)) {
          return track;
        }
      }

      // If all tracks have collisions, we'll create a new track
      return undefined;
    }

    // For RIPPLE and OVERLAP modes, use the first video track
    return videoTracks[0];
  }

  /**
   * Applies video-specific modifications to the element
   */
  private applyVideoSpecificModifications(): boolean {
    try {
      const modifications: Record<string, any> = {};

      // Apply volume setting (convert to percentage string format)
      if (this.options.volume !== undefined) {
        modifications.volume = `${this.options.volume}%`;
      }

      // Apply fit setting
      if (this.options.fit !== undefined) {
        modifications.fit = this.options.fit;
      }

      // Apply loop setting
      if (this.options.loop !== undefined) {
        modifications.loop = this.options.loop;
      }

      // Apply auto-fade effects (use Creatomate property names)
      if (this.options.autoFade) {
        modifications.audio_fade_in = 1.0; // 1 second fade in
        modifications.audio_fade_out = 1.0; // 1 second fade out
      }

      // Apply all modifications at once using the proper update method
      if (Object.keys(modifications).length > 0) {
        this.element.update(modifications);
        
        VideoEditorLogger.logTimelineOperation('add_video_element_modifications_applied', {
          action: 'applied_video_modifications',
          modifications,
        });
      }

      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to apply video modifications',
        details: error,
      });
      return false;
    }
  }

  /**
   * Creates a new video track specifically for video content
   */
  protected createNewTrack(): Track | undefined {
    try {
      const trackManager = this.editor.getTrackManager();
      this.createdTrack = trackManager.createTrack('video');
      this.trackCreated = true;

      VideoEditorLogger.logTimelineOperation('add_video_element_track_created', {
        action: 'video_track_created',
        trackId: this.createdTrack.id,
        trackType: 'video',
      });

      return this.createdTrack;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to create new video track',
        details: error,
      });
      return undefined;
    }
  }
}